import { useCallback, useEffect, useState } from 'react'
import { api, ApiError } from '../lib/api.js'
import { dataHora, podeNoPapel } from '../lib/formato.js'
import { Diff } from '../componentes/Diff.jsx'

const RASCUNHO = 'rascunho'

/**
 * Historico + diff. Duas comparacoes: rascunho x vigente (o caso do dia a dia,
 * "o que vou publicar?") e versao x versao (auditoria, "o que mudou entre v3 e
 * v7?"). O conteudo de cada versao e buscado sob demanda e memorizado — a lista
 * nao carrega 30k chars por linha.
 */
export function Historico({ agente, papel, usuario }) {
  const [versoes, setVersoes] = useState([])
  const [conteudos, setConteudos] = useState({})
  const [base, setBase] = useState(null)
  const [comparado, setComparado] = useState(RASCUNHO)
  const [erro, setErro] = useState(null)
  const [ocupado, setOcupado] = useState(false)
  const [aviso, setAviso] = useState(null)

  const podeRestaurar = podeNoPapel(papel, 'editor')

  const carregar = useCallback(async () => {
    setErro(null)
    try {
      const [lista, rascunho] = await Promise.all([
        api.get(`/agentes/${agente}/versoes`),
        api.get(`/agentes/${agente}/rascunho`),
      ])
      setVersoes(lista.versoes)
      setConteudos({ [RASCUNHO]: rascunho.conteudo, _lock: rascunho.lock })
      const ativa = lista.versoes.find((v) => v.ativo)
      setBase(ativa ? String(ativa.versao) : lista.versoes[0] ? String(lista.versoes[0].versao) : null)
      setComparado(RASCUNHO)
    } catch (falha) {
      setErro(falha.message)
    }
  }, [agente])

  useEffect(() => {
    carregar()
  }, [carregar])

  const buscarConteudo = useCallback(
    async (chave) => {
      if (chave === null || chave === RASCUNHO || conteudos[chave] !== undefined) return
      try {
        const versao = await api.get(`/agentes/${agente}/versoes/${chave}`)
        setConteudos((anterior) => ({ ...anterior, [chave]: versao.conteudo }))
      } catch (falha) {
        setErro(falha.message)
      }
    },
    [agente, conteudos],
  )

  useEffect(() => {
    buscarConteudo(base)
    buscarConteudo(comparado)
  }, [base, comparado, buscarConteudo])

  async function restaurar(versao) {
    setOcupado(true)
    setAviso(null)
    try {
      await api.post(`/agentes/${agente}/restaurar`, { versao })
      await carregar()
      setAviso(`Versão ${versao} copiada para o rascunho. Publique para que ela vire a vigente.`)
    } catch (falha) {
      const semLock = falha instanceof ApiError && (falha.status === 403 || falha.codigo === 'lock_de_outro')
      setErro(semLock ? `${falha.message} — assuma a edição na aba Editor antes de restaurar.` : falha.message)
    } finally {
      setOcupado(false)
    }
  }

  const rotulo = (chave) => (chave === RASCUNHO ? 'rascunho' : `v${chave}`)
  const prontos = conteudos[base] !== undefined && conteudos[comparado] !== undefined

  return (
    <section className="historico">
      {erro && <p className="alerta alerta--falha">{erro}</p>}
      {aviso && <p className="alerta alerta--ok">{aviso}</p>}

      <div className="historico__comparador">
        <label className="campo campo--inline">
          <span>Comparar</span>
          <select value={base ?? ''} onChange={(e) => setBase(e.target.value)}>
            {versoes.map((v) => (
              <option key={v.versao} value={String(v.versao)}>
                v{v.versao} {v.ativo ? '(vigente)' : ''}
              </option>
            ))}
          </select>
        </label>

        <span className="historico__seta">→</span>

        <label className="campo campo--inline">
          <span>com</span>
          <select value={comparado} onChange={(e) => setComparado(e.target.value)}>
            <option value={RASCUNHO}>rascunho</option>
            {versoes.map((v) => (
              <option key={v.versao} value={String(v.versao)}>
                v{v.versao} {v.ativo ? '(vigente)' : ''}
              </option>
            ))}
          </select>
        </label>
      </div>

      {prontos ? (
        <Diff
          anterior={conteudos[base]}
          atual={conteudos[comparado]}
          rotuloAnterior={rotulo(base)}
          rotuloAtual={rotulo(comparado)}
        />
      ) : (
        <p className="ajuda">Carregando conteúdo…</p>
      )}

      <table className="tabela">
        <thead>
          <tr>
            <th>Versão</th>
            <th>Publicada em</th>
            <th>Por</th>
            <th>Comentário</th>
            <th>Tamanho</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {versoes.map((v) => (
            <tr key={v.versao} className={v.ativo ? 'linha--ativa' : undefined}>
              <td>
                v{v.versao} {v.ativo && <span className="etiqueta etiqueta--ok">vigente</span>}
              </td>
              <td>{dataHora(v.criado_em)}</td>
              <td>{v.criado_por ?? '—'}</td>
              <td className="celula--comentario">{v.comentario ?? '—'}</td>
              <td>{Number(v.tamanho).toLocaleString('pt-BR')}</td>
              <td>
                <button
                  className="botao botao--pequeno"
                  onClick={() => setComparado(String(v.versao))}
                  disabled={String(v.versao) === comparado}
                >
                  ver diff
                </button>
                {podeRestaurar && !v.ativo && (
                  <button
                    className="botao botao--pequeno"
                    onClick={() => restaurar(v.versao)}
                    disabled={ocupado || conteudos._lock?.detentor !== usuario.email}
                    title={
                      conteudos._lock?.detentor === usuario.email
                        ? 'Copia esta versão para o rascunho'
                        : 'Assuma a edição na aba Editor para restaurar'
                    }
                  >
                    restaurar
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  )
}
